import { Box, Icon, Typography } from "@outschool/backpack";
import { faComment } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { BlockScopedMessageInput } from "@outschool/ui-components-classroom";
import React from "react";

import ThreadViewLayout from "./ThreadViewLayout";
import { Leader, LearnerInfo, SectionInfo } from "./types";

type NewThreadViewProps = {
  section: SectionInfo;
  learner: LearnerInfo;
  leader: Leader | null;
  postTitle?: string;
  hideBackButton?: boolean;
  onBack: () => void;
  onClose: () => void;
  onThreadCreated: (threadUid: string) => void;
} & (
  | { classPostUid: string; learningAssignmentClassPostUid?: never }
  | { classPostUid?: never; learningAssignmentClassPostUid: string }
);

export function getBlockScopedPostTarget(
  props:
    | { classPostUid: string; learningAssignmentClassPostUid?: never }
    | { classPostUid?: never; learningAssignmentClassPostUid: string },
) {
  return props.classPostUid
    ? { classPostUid: props.classPostUid }
    : {
        learningAssignmentClassPostUid: props.learningAssignmentClassPostUid!,
      };
}

export default function NewThreadView(props: NewThreadViewProps) {
  const { section, learner, leader, onBack, onClose, onThreadCreated } = props;
  const { t } = useTranslation(
    "learnerApp\\MessageTeacherButton\\NewThreadView",
  );
  const teacherName = leader?.name?.trim() || t("Teacher");
  const blockScopedPostTarget = getBlockScopedPostTarget(props);
  const normalizedPostTitle = props.postTitle?.trim() ?? "";
  const hasPostTitle = normalizedPostTitle.length > 0;

  return (
    <ThreadViewLayout
      section={section}
      leader={leader}
      postTitle={
        hasPostTitle
          ? normalizedPostTitle
          : t("Message {{teacherName}}", { teacherName })
      }
      postSubtitle={
        hasPostTitle
          ? t("Ask your teacher a question about this post.")
          : t("Ask anything about the course")
      }
      showBackButton={!props.hideBackButton}
      onBack={onBack}
      onClose={onClose}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 24,
          gap: 5,
        }}
      >
        <Icon
          icon={faComment}
          sx={{
            color: "neutral.400",
            fontSize: "4rem",
            mb: 4,
          }}
        />
        <Typography variant="h6" color="text.secondary">
          {t("Start a conversation")}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center" }}
        >
          {t("Type your question below")}
        </Typography>
      </Box>

      <BlockScopedMessageInput
        placeholder={t("Message your teacher")}
        sectionUid={section.uid}
        learnerUid={learner.uid}
        {...blockScopedPostTarget}
        sender={{
          uid: learner.uid,
          name: learner.name ?? "",
          photo: null,
          isLearner: true,
        }}
        onThreadCreated={onThreadCreated}
        sx={{ borderColor: "divider" }}
      />
    </ThreadViewLayout>
  );
}
